import Decimal from 'decimal.js-light';
import { useMemo } from 'react';
import { formatEther } from 'viem';

import { CHAIN_IDS } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import { GasPumpIcon } from '@/ui/Icons/GasPumpIcon';
import { MatchaFlatGreenIcon } from '@/ui/Icons/MatchaFlatGreenIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipBottomSheet,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { Fees } from '@/utils/0x.types';
import { TokenResult } from '@/utils/0x/token-registry.types';
import { routes } from '@/utils/routes';
import { lamportToSol } from '@/utils/solana';

import { GaslessGasFeeUsd } from './FeeAmount';

type PriceRecapMarketModeProps = {
  isCurrentSwapGasless: boolean;
  sellToken: TokenResult;
  buyToken: TokenResult;
  fees: Fees | undefined;
  totalNetworkFee?: string;
  nativeTokenUsd: number;
  tokenUsdPrices: { buyTokenUsd: number; sellTokenUsd: number };
};

export const PriceRecapMarketMode = ({
  isCurrentSwapGasless,
  sellToken,
  buyToken,
  fees,
  totalNetworkFee,
  nativeTokenUsd,
  tokenUsdPrices,
}: PriceRecapMarketModeProps) => {
  const { formatFiat } = useFiatDisplay();

  const networkFee = useMemo(() => {
    if (!totalNetworkFee) return undefined;

    const baseValue = new Decimal(
      sellToken.chainId === CHAIN_IDS.SOLANA
        ? lamportToSol(totalNetworkFee)
        : formatEther(BigInt(totalNetworkFee)),
    );
    return formatFiat(baseValue.mul(nativeTokenUsd));
  }, [totalNetworkFee, sellToken.chainId, nativeTokenUsd, formatFiat]);

  if (!isCurrentSwapGasless) {
    return (
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: ['text-token-gray-800', 'flex', 'items-center'],
        })}
      >
        <GasPumpIcon />
        <span className="px-1 capitalize">Estimate</span>
        {networkFee && <span className="text-token-gray-1600 pl-1"> · {networkFee}</span>}
      </div>
    );
  }

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <div
          className={fontStyles({
            variant: 'BODY_MD',
            class: ['text-token-gray-800', 'flex', 'items-center'],
          })}
        >
          <MatchaFlatGreenIcon />
          &nbsp;<span className="text-token-green-1000 font-medium">Free</span>
          <span className="text-token-gray-1600">
            {fees && (
              <>
                &nbsp;·&nbsp;
                <GaslessGasFeeUsd
                  fees={fees}
                  sellToken={sellToken}
                  buyToken={buyToken}
                  tokenUsdPrices={tokenUsdPrices}
                />
              </>
            )}
          </span>
        </div>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>
        <div className="flex flex-col gap-2">
          <div
            className={fontStyles({
              variant: 'LABEL_MD',
              class: 'text-token-gray-1600 flex items-center gap-1',
            })}
          >
            <MatchaFlatGreenIcon />
            <span>Matcha Auto</span>
          </div>
          <div className="text-token-gray-800">
            Your trading autopilot companion. When available it takes care of all the complex
            execution settings of your swap like Network Fee.{' '}
            <ExternalLink
              href={routes.MATCHA_AUTO_BLOG_POST}
              className="text-token-purple-900 hover:underline"
            >
              Learn more
            </ExternalLink>
          </div>
        </div>
      </MatchaTooltipContent>
      <MatchaTooltipPopover className="py-3" />
      <MatchaTooltipBottomSheet header="Matcha Auto" />
    </MatchaTooltip>
  );
};

type PriceRecapCrossChainModeProps = {
  totalNetworkFee: string;
  nativeTokenUsd: number;
  chainType: 'evm' | 'svm';
};

export const PriceRecapCrossChainMode = ({
  totalNetworkFee,
  nativeTokenUsd,
  chainType,
}: PriceRecapCrossChainModeProps) => {
  const { formatFiat } = useFiatDisplay();

  const networkFeeUsd = useMemo(() => {
    const baseValue = totalNetworkFee
      ? new Decimal(
          chainType === 'svm'
            ? lamportToSol(totalNetworkFee)
            : formatEther(BigInt(totalNetworkFee)),
        )
      : new Decimal(0);
    return formatFiat(baseValue.mul(nativeTokenUsd));
  }, [totalNetworkFee, nativeTokenUsd, chainType, formatFiat]);

  return (
    <div
      className={fontStyles({
        variant: 'BODY_MD',
        class: ['text-token-gray-800', 'flex', 'items-center'],
      })}
    >
      <GasPumpIcon />
      &nbsp;Estimate ·&nbsp;
      <span className="text-token-gray-1600">{networkFeeUsd}</span>
    </div>
  );
};
